// Authentik OAuth2/OIDC authentication implementation

import type { AuthentikWindow, TokenResponse, UserInfo } from '../../types/common'
import { criticalLogger, logger } from '../../utils/logger'

import { AuthentikAuthError } from '../../types/auth'
import type { AuthentikUser } from '../../types/auth'
import authService from './auth'
import { jwtService } from './jwtService'

// Authentik OAuth2 configuration interface
interface AuthentikConfig {
  clientId: string
  authorizationEndpoint: string
  tokenEndpoint: string
  userInfoEndpoint: string
  logoutEndpoint: string
  redirectUri: string
  scope?: string
}

// Default scopes for Authentik
const DEFAULT_SCOPES = 'openid profile email offline_access'

// Generate a random state for OAuth2 flow
const generateState = (): string => {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

// Generate PKCE code verifier
const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// Generate PKCE code challenge
const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// Store OAuth2 state and PKCE verifier in session storage
const storeOAuthData = (state: string, codeVerifier: string): void => {
  try {
    sessionStorage.setItem('authentik_oauth_state', state)
    sessionStorage.setItem('authentik_code_verifier', codeVerifier)
    logger.info('Stored OAuth data in session storage')
  } catch (error) {
    criticalLogger.error('Failed to store OAuth data:', error)
    throw new AuthentikAuthError('Failed to store OAuth data', error)
  }
}

// Retrieve and clear OAuth2 data from session storage
const retrieveOAuthData = (): { state: string | null; codeVerifier: string | null } => {
  try {
    const state = sessionStorage.getItem('authentik_oauth_state')
    const codeVerifier = sessionStorage.getItem('authentik_code_verifier')

    logger.info('Retrieved OAuth data from session storage:', {
      hasState: !!state,
      hasCodeVerifier: !!codeVerifier
    })

    // Clear the data after retrieval
    sessionStorage.removeItem('authentik_oauth_state')
    sessionStorage.removeItem('authentik_code_verifier')

    return { state, codeVerifier }
  } catch (error) {
    criticalLogger.error('Failed to retrieve OAuth data:', error)
    throw new AuthentikAuthError('Failed to retrieve OAuth data', error)
  }
}

// Load Authentik configuration from environment or config
export const loadAuthentikAuth = async (): Promise<void> => {
  try {
    const config = loadAuthentikConfig()
    logger.info('Authentik auth initialized with config:', {
      clientId: config.clientId,
      authEndpoint: config.authorizationEndpoint,
      tokenEndpoint: config.tokenEndpoint
    })
  } catch (error) {
    criticalLogger.error('Failed to initialize Authentik auth:', error)
    throw error
  }
}

// Load Authentik configuration from environment or config
const loadAuthentikConfig = (): AuthentikConfig => {
  logger.info('Loading Authentik configuration...')

  const authentikWindow = window as AuthentikWindow

  logger.info('Window config status:', {
    hasClientId: !!authentikWindow.authentikClientId,
    hasAuthEndpoint: !!authentikWindow.authentikAuthorizationEndpoint,
    hasTokenEndpoint: !!authentikWindow.authentikTokenEndpoint,
    hasScope: !!authentikWindow.authentikScope
  })

  const clientId = authentikWindow.authentikClientId

  if (!clientId) {
    criticalLogger.error('Authentik client ID is missing. Window config status:', {
      windowLocation: window.location.href,
      configStatus: {
        hasClientId: !!clientId,
        hasAuthEndpoint: !!authentikWindow.authentikAuthorizationEndpoint,
        hasTokenEndpoint: !!authentikWindow.authentikTokenEndpoint,
        hasScope: !!authentikWindow.authentikScope
      }
    })
    throw new AuthentikAuthError('Authentik client ID is not configured')
  }

  const config: AuthentikConfig = {
    clientId,
    authorizationEndpoint: authentikWindow.authentikAuthorizationEndpoint || '',
    tokenEndpoint: authentikWindow.authentikTokenEndpoint || '',
    userInfoEndpoint: authentikWindow.authentikUserInfoEndpoint || '',
    logoutEndpoint: authentikWindow.authentikLogoutEndpoint || '',
    redirectUri: `${window.location.protocol}//${window.location.host}/login`,
    scope: authentikWindow.authentikScope || DEFAULT_SCOPES
  }

  logger.info('Loaded Authentik config:', {
    hasClientId: !!config.clientId,
    hasAuthEndpoint: !!config.authorizationEndpoint,
    hasTokenEndpoint: !!config.tokenEndpoint,
    hasUserInfoEndpoint: !!config.userInfoEndpoint,
    hasLogoutEndpoint: !!config.logoutEndpoint,
    redirectUri: config.redirectUri,
    scope: config.scope
  })

  // Validate configuration
  if (!config.clientId || !config.authorizationEndpoint || !config.tokenEndpoint) {
    criticalLogger.error('Authentik configuration is incomplete:', {
      hasClientId: !!config.clientId,
      hasAuthEndpoint: !!config.authorizationEndpoint,
      hasTokenEndpoint: !!config.tokenEndpoint
    })
    throw new AuthentikAuthError('Authentik configuration is incomplete')
  }

  return config
}

// Trigger Authentik OAuth2 login flow
export const triggerAuthentikSignIn = async (): Promise<void> => {
  try {
    const config = loadAuthentikConfig()
    const state = generateState()
    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    // Store state and verifier for later verification
    storeOAuthData(state, codeVerifier)

    // Debug logging
    logger.info('Authentik Configuration:', {
      redirectUri: config.redirectUri,
      origin: window.location.origin,
      fullUrl: window.location.href
    })

    // Build authorization URL with PKCE
    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: 'code',
      redirect_uri: config.redirectUri,
      scope: config.scope || DEFAULT_SCOPES,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    })

    // Debug logging - mask sensitive data
    logger.info('Authorization URL generated', {
      endpoint: config.authorizationEndpoint,
      hasClientId: !!config.clientId,
      redirectUri: config.redirectUri,
      scope: config.scope || DEFAULT_SCOPES
    })

    // Redirect to Authentik authorization endpoint
    window.location.href = `${config.authorizationEndpoint}?${params.toString()}`
  } catch (error) {
    throw new AuthentikAuthError('Failed to initiate Authentik sign-in', error)
  }
}

// Handle OAuth2 callback and exchange code for tokens
export const handleAuthentikCallback = async (
  code: string,
  state: string
): Promise<AuthentikUser | null> => {
  try {
    // Validate input parameters
    if (!securityValidation.validateAuthCode(code)) {
      throw new AuthentikAuthError('Invalid authorization code format')
    }

    if (!securityValidation.validateState(state)) {
      throw new AuthentikAuthError('Invalid state parameter format')
    }

    logger.info('Starting Authentik callback handling:', {
      hasCode: !!code,
      codeLength: code.length,
      hasState: !!state
    })

    const { state: storedState, codeVerifier } = retrieveOAuthData()
    logger.info('Retrieved OAuth data:', {
      hasStoredState: !!storedState,
      hasCodeVerifier: !!codeVerifier
    })

    // Verify state to prevent CSRF attacks (timing-safe comparison)
    if (!storedState || !constantTimeStringCompare(storedState, state)) {
      criticalLogger.error('OAuth state mismatch - possible CSRF attack')
      throw new AuthentikAuthError('Invalid OAuth state - security violation detected')
    }

    if (!codeVerifier) {
      throw new AuthentikAuthError('Missing PKCE code verifier')
    }

    const config = loadAuthentikConfig()

    // Validate configuration URLs
    securityValidation.validateConfigUrls(config)

    logger.info('Loaded Authentik config for callback:', {
      tokenEndpoint: config.tokenEndpoint,
      redirectUri: config.redirectUri,
      hasClientId: !!config.clientId
    })

    // Prepare token exchange request
    const tokenRequestParams = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: config.redirectUri,
      client_id: config.clientId,
      code_verifier: codeVerifier,
      response_type: 'code',
      scope: config.scope || DEFAULT_SCOPES
    })

    logger.info('Token exchange request prepared:', {
      grant_type: 'authorization_code',
      hasCode: !!code,
      redirect_uri: config.redirectUri,
      hasClientId: !!config.clientId,
      hasCodeVerifier: !!codeVerifier,
      response_type: 'code',
      scope: config.scope || DEFAULT_SCOPES
    })

    // Exchange authorization code for tokens using PKCE (no client secret needed)
    const tokenResponse = await fetch(config.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      },
      body: tokenRequestParams
    })

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json().catch(() => ({}))
      logger.error('Token exchange failed:', {
        status: tokenResponse.status,
        statusText: tokenResponse.statusText,
        errorData,
        requestUrl: config.tokenEndpoint,
        requestHeaders: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        }
      })
      throw new AuthentikAuthError(
        `Token exchange failed: ${errorData.error || tokenResponse.statusText}`
      )
    }

    const tokens = await tokenResponse.json()
    logger.info('Successfully received tokens')
    logger.info('Token response details:', {
      hasAccessToken: !!tokens.access_token,
      hasRefreshToken: !!tokens.refresh_token,
      hasIdToken: !!tokens.id_token,
      tokenType: tokens.token_type,
      expiresIn: tokens.expires_in,
      scope: tokens.scope,
      fullResponse: tokens
    })

    // Fetch user info if endpoint is configured
    if (config.userInfoEndpoint && tokens.access_token) {
      logger.info('Fetching user info from:', config.userInfoEndpoint)
      const userInfo = await fetchUserInfo(config.userInfoEndpoint, tokens.access_token)

      // Store tokens securely
      secureTokenStorage.store(tokens)

      return createAuthentikUser(userInfo, tokens)
    }

    // If no user info endpoint, decode ID token for basic info
    logger.info('No user info endpoint, validating and decoding ID token')
    const idTokenPayload = await validateAndDecodeJWT(tokens.id_token, config)

    // Store tokens securely
    secureTokenStorage.store(tokens)

    return createAuthentikUserFromToken(idTokenPayload, tokens)
  } catch (error) {
    logger.error('Authentik callback error details:', error)
    throw new AuthentikAuthError('Failed to handle Authentik callback', error)
  }
}

// Fetch user information from Authentik
const fetchUserInfo = async (endpoint: string, accessToken: string): Promise<UserInfo> => {
  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json'
    }
  })

  if (!response.ok) {
    throw new AuthentikAuthError(`Failed to fetch user info: ${response.status}`)
  }

  const userInfo = await response.json()

  // Validate email domain
  if (!authService.isAllowedDomain(userInfo.email || '')) {
    throw new AuthentikAuthError('Invalid email domain')
  }

  return userInfo
}

// Secure JWT validation using JWT service
const validateAndDecodeJWT = async (token: string, config: AuthentikConfig): Promise<UserInfo> => {
  try {
    const baseUrl = config.authorizationEndpoint.replace('/application/o/authorize/', '')
    const jwksUrl = `${baseUrl}/application/o/internal-devtools/jwks/`

    logger.info('Validating JWT token with Authentik')

    // Use JWT service for secure validation
    const result = await jwtService.validateToken(token, baseUrl, config.clientId, jwksUrl)

    if (!result.valid || !result.payload) {
      throw new AuthentikAuthError(result.error || 'JWT validation failed')
    }

    logger.info('JWT validation successful via JWT service')

    // Convert JWTPayload to UserInfo
    const userInfo: UserInfo = {
      sub: result.payload.sub,
      email: typeof result.payload.email === 'string' ? result.payload.email : undefined,
      name: typeof result.payload.name === 'string' ? result.payload.name : undefined,
      preferred_username:
        typeof result.payload.preferred_username === 'string'
          ? result.payload.preferred_username
          : undefined,
      email_verified:
        typeof result.payload.email_verified === 'boolean'
          ? result.payload.email_verified
          : undefined,
      groups: Array.isArray(result.payload.groups) ? result.payload.groups : undefined
    }

    return userInfo
  } catch (error) {
    criticalLogger.error('JWT validation failed:', error)
    throw error instanceof AuthentikAuthError
      ? error
      : new AuthentikAuthError('JWT validation failed')
  }
}

// Create user object from Authentik user info
const createAuthentikUser = (userInfo: UserInfo, tokens: TokenResponse): AuthentikUser => {
  logger.info('Authentik User Info received:', {
    hasUserId: !!(userInfo.sub || userInfo.id),
    hasName: !!(userInfo.name || userInfo.preferred_username),
    hasEmail: !!userInfo.email,
    hasGroups: !!userInfo.groups
  })

  return {
    id: userInfo.sub || userInfo.id || '',
    name: userInfo.name || userInfo.preferred_username || 'User',
    email: userInfo.email || '',
    email_verified: userInfo.email_verified || false,
    groups: userInfo.groups || [],
    provider: 'authentik',
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token
  }
}

// Create user object from ID token payload
const createAuthentikUserFromToken = (
  tokenPayload: UserInfo,
  tokens: TokenResponse
): AuthentikUser => {
  logger.info('Authentik Token Payload received:', {
    hasUserId: !!tokenPayload.sub,
    hasName: !!(tokenPayload.name || tokenPayload.preferred_username),
    hasEmail: !!tokenPayload.email,
    hasGroups: !!tokenPayload.groups
  })

  return {
    id: tokenPayload.sub || '',
    name: tokenPayload.name || tokenPayload.preferred_username || 'User',
    email: tokenPayload.email || '',
    email_verified: tokenPayload.email_verified || false,
    groups: tokenPayload.groups || [],
    provider: 'authentik',
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token
  }
}

// Sign out from Authentik
export const signOutAuthentik = async (): Promise<void> => {
  try {
    // Clear all secure token storage first
    secureTokenStorage.clear()

    // Try to load config for remote logout, but don't fail if it's not available
    try {
      const config = loadAuthentikConfig()

      if (config.logoutEndpoint) {
        // Build logout URL with post-logout redirect
        const params = new URLSearchParams({
          client_id: config.clientId,
          post_logout_redirect_uri: window.location.origin
        })

        // Redirect to Authentik logout endpoint
        window.location.href = `${config.logoutEndpoint}?${params.toString()}`
        return
      }
    } catch (configError) {
      // Config loading failed, but we've already cleared local data
      // Log the error but don't fail the signout process
      logger.error(
        'Failed to load Authentik config during signout, proceeding with local logout only:',
        configError
      )
    }

    // If we reach here, either no logout endpoint or config loading failed
    // Just redirect to login page since local data is already cleared
    window.location.href = '/login'
  } catch (error) {
    // If everything fails, at least clear local data and redirect
    secureTokenStorage.clear()
    window.location.href = '/login'

    throw new AuthentikAuthError('Failed to sign out from Authentik', error)
  }
}

// Check if user is authenticated with Authentik
export const isAuthentikAuthenticated = (): boolean => {
  return secureTokenStorage.hasValidTokens()
}

// Initialize token refresh scheduling on app startup
// This should be called when the app loads to ensure refresh timeouts are restored
// after page reload/navigation
export const initializeTokenRefresh = (): void => {
  try {
    const stored = localStorage.getItem('authentik_secure_tokens')
    if (!stored) return

    const tokenData = JSON.parse(stored)

    // If we have a refresh token and no scheduled refresh, set one up
    if (tokenData.refreshToken && !secureTokenStorage.refreshTimeoutId) {
      const timeUntilExpiry = tokenData.expiresAt - Date.now()

      if (timeUntilExpiry > 0) {
        const expiresInSeconds = Math.floor(timeUntilExpiry / 1000)
        logger.info('Initializing token refresh on app startup', {
          timeUntilExpiry: `${Math.round(timeUntilExpiry / 1000 / 60)} minutes`
        })
        secureTokenStorage.scheduleTokenRefresh(expiresInSeconds)
      } else {
        // Token already expired, trigger immediate refresh
        logger.info('Token already expired on app startup, triggering immediate refresh')
        secureTokenStorage.attemptTokenRefresh(atob(tokenData.refreshToken))
      }
    }
  } catch (error) {
    logger.error('Failed to initialize token refresh:', error)
  }
}

// Refresh access token using refresh token
export const refreshAuthentikToken = async (refreshToken: string): Promise<TokenResponse> => {
  try {
    const config = loadAuthentikConfig()

    logger.info('Attempting to refresh access token', {
      tokenEndpoint: config.tokenEndpoint,
      hasRefreshToken: !!refreshToken,
      refreshTokenLength: refreshToken?.length
    })

    const response = await fetch(config.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: config.clientId
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      criticalLogger.error('Token refresh failed', {
        status: response.status,
        statusText: response.statusText,
        error: errorData.error,
        errorDescription: errorData.error_description,
        // Common causes: refresh_token expired, revoked, or invalid
        hint: response.status === 400 ? 'Refresh token may have expired or been revoked' : undefined
      })
      throw new AuthentikAuthError(
        `Failed to refresh token: ${errorData.error_description || errorData.error || response.statusText}`
      )
    }

    const tokens = await response.json()
    logger.info('Token refresh successful', {
      hasNewAccessToken: !!tokens.access_token,
      hasNewRefreshToken: !!tokens.refresh_token,
      expiresIn: tokens.expires_in
    })

    return tokens
  } catch (error) {
    if (error instanceof AuthentikAuthError) {
      throw error
    }
    criticalLogger.error('Token refresh network error:', error)
    throw new AuthentikAuthError('Failed to refresh Authentik token - network error', error)
  }
}

// Enhanced secure token storage with expiry management
const secureTokenStorage = {
  // Store tokens with expiration and basic obfuscation
  store: (tokens: TokenResponse): void => {
    try {
      const expiresAt = Date.now() + (tokens.expires_in || 3600) * 1000
      const tokenData = {
        tokens: btoa(JSON.stringify(tokens)), // Basic obfuscation
        expiresAt,
        timestamp: Date.now(),
        refreshToken: tokens.refresh_token ? btoa(tokens.refresh_token) : null
      }

      // Use localStorage so tokens persist across tabs/windows
      localStorage.setItem('authentik_secure_tokens', JSON.stringify(tokenData))

      // Clear any old legacy tokens
      localStorage.removeItem('authentik_tokens')

      // Set up automatic refresh if refresh token available
      if (tokens.refresh_token) {
        secureTokenStorage.scheduleTokenRefresh(tokens.expires_in || 3600)
      }

      logger.info('Tokens stored securely with expiration and refresh scheduling')
    } catch (error) {
      criticalLogger.error('Failed to store tokens securely:', error)
      throw new AuthentikAuthError('Failed to store authentication tokens')
    }
  },

  // Retrieve and validate tokens
  retrieve: (): TokenResponse | null => {
    try {
      const stored = localStorage.getItem('authentik_secure_tokens')
      if (!stored) return null

      const tokenData = JSON.parse(stored)

      // Check if tokens have expired
      if (Date.now() >= tokenData.expiresAt) {
        logger.info('Stored tokens have expired, attempting refresh')

        // Try to refresh tokens if refresh token available
        if (tokenData.refreshToken) {
          secureTokenStorage.attemptTokenRefresh(atob(tokenData.refreshToken))
          return null // Return null while refresh is in progress
        }

        secureTokenStorage.clear()
        return null
      }

      // Calculate time until expiry
      const timeUntilExpiry = tokenData.expiresAt - Date.now()

      // Re-schedule automatic refresh if not already scheduled (e.g., after page reload)
      // This ensures the refresh timeout is restored after page navigation/reload
      if (!secureTokenStorage.refreshTimeoutId && tokenData.refreshToken) {
        const expiresInSeconds = Math.floor(timeUntilExpiry / 1000)
        logger.info('Re-scheduling token refresh after page load')
        secureTokenStorage.scheduleTokenRefresh(expiresInSeconds)
      }

      // Check if tokens are close to expiry (within 5 minutes)
      if (timeUntilExpiry < 5 * 60 * 1000 && tokenData.refreshToken) {
        logger.info('Tokens expiring soon, triggering refresh')
        secureTokenStorage.attemptTokenRefresh(atob(tokenData.refreshToken))
      }

      // Decode tokens
      const tokens = JSON.parse(atob(tokenData.tokens))
      return tokens
    } catch (error) {
      logger.error('Failed to retrieve tokens, clearing storage:', error)
      secureTokenStorage.clear()
      return null
    }
  },

  // Clear all token storage
  clear: (): void => {
    localStorage.removeItem('authentik_secure_tokens')
    localStorage.removeItem('authentik_tokens') // Legacy cleanup
    sessionStorage.removeItem('authentik_oauth_state')
    sessionStorage.removeItem('authentik_code_verifier')

    // Clear any scheduled refresh
    if (secureTokenStorage.refreshTimeoutId) {
      clearTimeout(secureTokenStorage.refreshTimeoutId)
      secureTokenStorage.refreshTimeoutId = null
    }
  },

  // Check if valid tokens exist
  hasValidTokens: (): boolean => {
    return secureTokenStorage.retrieve() !== null
  },

  // Internal refresh management
  refreshTimeoutId: null as NodeJS.Timeout | null,
  refreshInProgress: false,

  // Schedule automatic token refresh
  scheduleTokenRefresh: (expiresInSeconds: number): void => {
    // Schedule refresh 5 minutes before expiry
    const refreshTime = Math.max((expiresInSeconds - 300) * 1000, 60000) // At least 1 minute

    if (secureTokenStorage.refreshTimeoutId) {
      clearTimeout(secureTokenStorage.refreshTimeoutId)
    }

    secureTokenStorage.refreshTimeoutId = setTimeout(() => {
      const stored = localStorage.getItem('authentik_secure_tokens')
      if (stored) {
        const tokenData = JSON.parse(stored)
        if (tokenData.refreshToken) {
          secureTokenStorage.attemptTokenRefresh(atob(tokenData.refreshToken))
        }
      }
    }, refreshTime)

    logger.info(`Token refresh scheduled in ${refreshTime / 1000} seconds`)
  },

  // Attempt to refresh tokens
  attemptTokenRefresh: async (refreshToken: string): Promise<void> => {
    if (secureTokenStorage.refreshInProgress) {
      logger.info('Token refresh already in progress')
      return
    }

    secureTokenStorage.refreshInProgress = true

    try {
      logger.info('Attempting to refresh authentication tokens')
      const newTokens = await refreshAuthentikToken(refreshToken)

      if (newTokens) {
        secureTokenStorage.store(newTokens)
        logger.info('Tokens refreshed successfully')
      }
    } catch (error) {
      logger.error('Failed to refresh tokens:', error)
      // Don't clear tokens on refresh failure - they might still be valid for a bit
    } finally {
      secureTokenStorage.refreshInProgress = false
    }
  }
}

// Clear only DevTools authentication state. The Authentik SSO session remains
// active, so other applications using the same identity provider stay signed in.
export const clearLocalAuthentikSession = (): void => {
  secureTokenStorage.clear()
}

// Constant-time string comparison to prevent timing attacks
const constantTimeStringCompare = (a: string, b: string): boolean => {
  if (a.length !== b.length) {
    return false
  }

  let result = 0
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i)
  }

  return result === 0
}

// Security validation functions
const securityValidation = {
  // Validate OAuth state parameter
  validateState: (state: string): boolean => {
    if (!state || typeof state !== 'string') {
      return false
    }
    // State should be at least 32 characters and alphanumeric
    return state.length >= 32 && /^[a-zA-Z0-9]+$/.test(state)
  },

  // Validate authorization code
  validateAuthCode: (code: string): boolean => {
    if (!code || typeof code !== 'string') {
      return false
    }
    // Auth codes should be substantial length and safe characters
    return code.length >= 10 && /^[a-zA-Z0-9\-_]+$/.test(code)
  },

  // Validate redirect URL for open redirect attacks
  validateRedirectUrl: (url: string, allowedOrigins: string[]): boolean => {
    try {
      const urlObj = new URL(url)

      // Only allow HTTPS in production
      if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
        return false
      }

      // Check if origin is in allowed list
      const origin = `${urlObj.protocol}//${urlObj.host}`
      return allowedOrigins.includes(origin)
    } catch {
      return false
    }
  },

  // Validate and sanitize scope parameter
  validateScope: (scope: string): string => {
    if (!scope || typeof scope !== 'string') {
      return DEFAULT_SCOPES
    }

    // Only allow known safe scopes
    const allowedScopes = ['openid', 'profile', 'email', 'offline_access']
    const requestedScopes = scope.split(' ').filter((s) => allowedScopes.includes(s))

    return requestedScopes.length > 0 ? requestedScopes.join(' ') : DEFAULT_SCOPES
  },

  // Validate configuration URLs
  validateConfigUrls: (config: AuthentikConfig): void => {
    const urls = [
      config.authorizationEndpoint,
      config.tokenEndpoint,
      config.userInfoEndpoint,
      config.logoutEndpoint
    ].filter(Boolean)

    for (const url of urls) {
      try {
        const urlObj = new URL(url)

        // Ensure HTTPS in production
        if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
          throw new AuthentikAuthError(`Insecure URL in production: ${url}`)
        }

        // Basic domain validation
        if (
          !urlObj.hostname ||
          (urlObj.hostname === 'localhost' && process.env.NODE_ENV === 'production')
        ) {
          throw new AuthentikAuthError(`Invalid hostname: ${urlObj.hostname}`)
        }
      } catch (error) {
        throw new AuthentikAuthError(`Invalid configuration URL: ${url}`)
      }
    }
  }
}

// Rate limiting for authentication attempts
const rateLimiter = {
  attempts: new Map<string, { count: number; lastAttempt: number; blocked: boolean }>(),

  // Check if IP/session is rate limited
  isRateLimited: (identifier = 'global'): boolean => {
    const now = Date.now()
    const attempt = rateLimiter.attempts.get(identifier)

    if (!attempt) return false

    // Reset counter after 15 minutes
    if (now - attempt.lastAttempt > 15 * 60 * 1000) {
      rateLimiter.attempts.delete(identifier)
      return false
    }

    // Block after 5 failed attempts
    return attempt.count >= 5
  },

  // Record failed attempt
  recordFailedAttempt: (identifier = 'global'): void => {
    const now = Date.now()
    const attempt = rateLimiter.attempts.get(identifier) || {
      count: 0,
      lastAttempt: 0,
      blocked: false
    }

    attempt.count++
    attempt.lastAttempt = now
    attempt.blocked = attempt.count >= 5

    rateLimiter.attempts.set(identifier, attempt)

    if (attempt.blocked) {
      criticalLogger.error(`Rate limit exceeded for identifier: ${identifier}`)
    }
  },

  // Reset attempts on successful login
  resetAttempts: (identifier = 'global'): void => {
    rateLimiter.attempts.delete(identifier)
  }
}
